import { navigate } from "gatsby";
import { useEffect, useRef } from "react";
import { useSnackbar } from "notistack";
import { useIsInDashboardRoutes } from "../../hooks/common.hooks";
import {
  useIsLoggedIn,
  useMyInfo,
  useIsCompanyAdmin,
} from "../../hooks/user.hooks";
import { use2FASetting } from "../../hooks/2fa-setting.hooks.ts";
import LoadingBackdrop from "../loading-backdrop";
import { USER_ROLE } from "../../commons/enum";

const UserAuthenticationNavigate = ({ children, location }) => {
  const { data: myInfo, isError, isLoading } = useMyInfo();
  const pathname = location.pathname.replace(/\//g, "");
  const isInDashboardRoutes = useIsInDashboardRoutes(pathname);
  const isLoggedIn = useIsLoggedIn();
  const isUserRole = myInfo?.userRole === USER_ROLE.USER;
  const { enqueueSnackbar } = useSnackbar();
  const hasRedirectedFor2FA = useRef(false);

  // Only check 2FA status when in dashboard routes and user info is loaded
  const shouldCheck2FA = isInDashboardRoutes && !isLoading && myInfo;
  const {
    data: twoFASetting,
    isLoading: is2FALoading,
    isSuccess: is2FASuccess,
  } = use2FASetting();

  const {
    data: isCompanyAdmin,
    isLoading: isCompanyAdminLoading,
    isSuccess: isCompanyAdminSuccess,
  } = useIsCompanyAdmin();

  useEffect(() => {
    if (!isInDashboardRoutes) {
      hasRedirectedFor2FA.current = false;
    }
  }, [isInDashboardRoutes]);

  useEffect(() => {
    if (
      shouldCheck2FA &&
      !is2FALoading &&
      !isCompanyAdminLoading &&
      is2FASuccess &&
      isCompanyAdminSuccess &&
      !hasRedirectedFor2FA.current
    ) {
      if (isCompanyAdmin && (!twoFASetting || twoFASetting === "DISABLED")) {
        hasRedirectedFor2FA.current = true;
        enqueueSnackbar(
          "Company admins must set up 2FA to access the dashboard",
          {
            variant: "warning",
            autoHideDuration: 5000,
          }
        );
        // Navigate to 2FA setup page
        navigate("/setup-2fa");
      }
    }
  }, [
    shouldCheck2FA,
    isCompanyAdmin,
    twoFASetting,
    is2FALoading,
    isCompanyAdminLoading,
    is2FASuccess,
    isCompanyAdminSuccess,
    enqueueSnackbar,
  ]);

  useEffect(() => {
    if (isInDashboardRoutes) {
      if (isError) {
        isLoggedIn ? navigate("/error-data") : navigate("/login");
      } else {
        isUserRole && navigate("/forbidden");
      }
    }
  }, [isError, isInDashboardRoutes, isLoggedIn, isUserRole]);

  if (isLoading) {
    return <LoadingBackdrop />;
  }

  if (isInDashboardRoutes && (is2FALoading || isCompanyAdminLoading)) {
    return <LoadingBackdrop />;
  }

  return children;
};

export default UserAuthenticationNavigate;
